import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { API_BASE } from '../../config/api';
import './AdminAvailability.css';

// Fallback slots if DB is empty
const DEFAULT_TIME_SLOTS = {
  morning: [
    { time: '08:00 - 09:00', id: 'm1' },
    { time: '09:00 - 10:00', id: 'm2' },
    { time: '10:00 - 11:00', id: 'm3' },
    { time: '11:00 - 12:00', id: 'm4' },
  ],
  evening: [
    { time: '14:00 - 15:00', id: 'e1' },
    { time: '15:00 - 16:00', id: 'e2' },
    { time: '16:00 - 17:00', id: 'e3' },
    { time: '17:00 - 18:00', id: 'e4' },
  ],
};

const AdminAvailability = () => {
  const [date, setDate] = useState(new Date());
  const [reservedSlots, setReservedSlots] = useState([]);
  const [unavailableSlots, setUnavailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS);

  const dateStr = useMemo(() => date.toISOString().slice(0, 10), [date]);

  // Load time slots from backend (same source used by booking system)
  useEffect(() => {
    axios
      .get(`${API_BASE}/api/services/time-slots`)
      .then((res) => {
        if (res.data && res.data.timeSlots) {
          const incoming = res.data.timeSlots;
          setTimeSlots({
            morning:
              Array.isArray(incoming.morning) && incoming.morning.length
                ? incoming.morning
                : DEFAULT_TIME_SLOTS.morning,
            evening:
              Array.isArray(incoming.evening) && incoming.evening.length
                ? incoming.evening
                : DEFAULT_TIME_SLOTS.evening,
          });
        }
      })
      .catch(() => {
        setTimeSlots(DEFAULT_TIME_SLOTS);
      });
  }, []);

  const loadAvailability = () => {
    setLoading(true);
    axios
      .get(`${API_BASE}/api/availability?date=${dateStr}`)
      .then((res) => {
        console.log(`Loaded availability for ${dateStr}:`, res.data);
        setReservedSlots(res.data.reservedSlots || []);
        setUnavailableSlots(res.data.unavailableSlots || []);
      })
      .catch((err) => {
        console.error('Error loading availability:', err);
        setReservedSlots([]);
        setUnavailableSlots([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAvailability();
  }, [dateStr]);

  const allSlots = [
    ...(timeSlots.morning || DEFAULT_TIME_SLOTS.morning),
    ...(timeSlots.evening || DEFAULT_TIME_SLOTS.evening),
  ];

  const allSlotIds = allSlots.map((s) => s.id);
  const isDayDisabled =
    allSlotIds.length > 0 &&
    allSlotIds.every((id) => unavailableSlots.includes(id));

  const toggleSlot = async (slotId) => {
    // Prevent toggling reserved slots
    if (reservedSlots.includes(slotId)) {
      alert('لا يمكن تغيير الأوقات المحجوزة');
      return;
    }
    
    const token = localStorage.getItem('adminToken');
    if (!token) {
      alert('يرجى تسجيل الدخول مرة أخرى');
      return;
    }

    setSaving(slotId);
    
    try {
      console.log(`Toggling slot in database: date=${dateStr}, slotId=${slotId}`);
      
      // Send toggle request - backend will handle adding if not exists and toggling is_available
      const response = await axios.post(
        `${API_BASE}/api/admin/availability`,
        { 
          date: dateStr, 
          slotId
          // No 'available' parameter - backend will toggle automatically
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Database toggle response:', response.data);
      
      if (response.data.ok) {
        console.log(`✅ Successfully toggled in database: ${response.data.message}`);
        console.log(`   Action: ${response.data.action}, is_available: ${response.data.is_available}`);
        
        // Always refresh from server to ensure UI matches database state
        await loadAvailability();
      }
    } catch (err) {
      console.error('❌ Database toggle error:', err);
      console.error('Error details:', err.response?.data);
      alert(err.response?.data?.error || err.response?.data?.details || 'حدث خطأ أثناء التحديث');
    } finally {
      setSaving(null);
    }
  };

  const setDayAvailability = async (available) => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      // eslint-disable-next-line no-alert
      alert('يرجى تسجيل الدخول مرة أخرى');
      return;
    }

    setSaving('day');
    try {
      await axios.post(
        `${API_BASE}/api/admin/availability/day`,
        {
          date: dateStr,
          available,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadAvailability();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(
        err.response?.data?.error ||
          err.response?.data?.details ||
          'حدث خطأ أثناء تحديث اليوم'
      );
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="admin-availability">
      <h1 className="admin-page-title">إدارة الأوقات</h1>
      <p className="admin-availability-desc">
        حدّدي التاريخ ثم انقري على أي وقت لتبديله بين متاح وغير متاح. الأوقات المحجوزة لا يمكن تغييرها.
      </p>

      <div className="admin-availability-layout">
        <div className="admin-availability-calendar">
          <Calendar onChange={setDate} value={date} locale="ar-EG" minDate={new Date()} />
        </div>

        <div className="admin-availability-slots">
          <h3>{date.toLocaleDateString('ar-EG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</h3>
          <div className="admin-day-toggle">
            <span>
              {isDayDisabled
                ? 'هذا اليوم معطّل بالكامل.'
                : 'هذا اليوم يستخدم الأوقات الافتراضية.'}
            </span>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setDayAvailability(isDayDisabled)}
              disabled={saving === 'day'}
            >
              {saving === 'day'
                ? 'جارٍ التحديث...'
                : isDayDisabled
                ? 'إعادة تفعيل اليوم'
                : 'تعطيل اليوم بالكامل'}
            </button>
          </div>
          {loading ? (
            <div className="admin-loading">جاري التحميل...</div>
          ) : (
            <div className="admin-slots-grid">
              {allSlots.map((slot) => {
                const isReserved = reservedSlots.includes(slot.id);
                const isUnavailable = unavailableSlots.includes(slot.id);
                const isSaving = saving === slot.id;

                let className = 'admin-slot-btn';
                if (isReserved) className += ' reserved';
                else if (isUnavailable) className += ' unavailable';
                else className += ' available';
                if (isSaving) className += ' saving';

                return (
                  <button
                    key={slot.id}
                    className={className}
                    onClick={() => toggleSlot(slot.id)}
                    disabled={isReserved || isSaving}
                  >
                    <span className="slot-time">{slot.time}</span>
                    <span className="slot-status">
                      {isReserved ? 'محجوز' : isUnavailable ? 'غير متاح' : 'متاح'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAvailability;
